/**
 * Public demo report registry (`/demo-report`).
 *
 * Every variant renders the REAL vendored v2 report components with a fixture
 * exported from a real production application and scrubbed of PII (names,
 * contact details, addresses, employers, account numbers, document images).
 * The fixtures live in `./fixtures/*.json`; the scrub tooling stays outside
 * the repo because it embeds the real→demo identity map.
 *
 * Fixture identity rules (relied on by the redirect routes and the vendored
 * components' own navigation):
 * - `application.propertyId === DEMO_PROPERTY_ID` for every fixture.
 * - `application.id` / member ids are `demo-<variant>` — see
 *   {@link demoVariantForApplication}.
 */
import type { CombinedReportData } from '#app/utils/application-detail-v2/combined-types'
import type { ApplicationDetailData } from '#app/utils/application-detail-v2/types'
import { overlayDemoFormFields } from './demo-form-overlay'
import adeyemi from './fixtures/adeyemi.json'
import bennettChloe from './fixtures/bennett-chloe.json'
import bennettHousehold from './fixtures/bennett-household.json'
import bennettMargaret from './fixtures/bennett-margaret.json'
import bergeron from './fixtures/bergeron.json'
import haddadHousehold from './fixtures/haddad-household.json'
import haddadKarim from './fixtures/haddad-karim.json'
import haddadLayla from './fixtures/haddad-layla.json'
import haddadOmar from './fixtures/haddad-omar.json'
import malone from './fixtures/malone.json'
import rahman from './fixtures/rahman.json'
import santos from './fixtures/santos.json'
import whitfield from './fixtures/whitfield.json'
import { overlayAttachedTemplates } from './overlay-attached-templates'
import { overlayHtmlSampleResults } from './overlay-html-sample'

export type ScoreDisplay = ApplicationDetailData['scoreDisplay']

export const DEMO_VARIANTS = [
	'adeyemi',
	'santos',
	'whitfield',
	'rahman',
	'bergeron',
	'malone',
	'haddad-household',
	'haddad-omar',
	'haddad-layla',
	'haddad-karim',
	'bennett-household',
	'bennett-chloe',
	'bennett-margaret',
] as const
export type DemoVariant = (typeof DEMO_VARIANTS)[number]

export const DEFAULT_DEMO_VARIANT: DemoVariant = 'santos'

/** Pre-rebuild `?variant=` values still emitted by the /demo screening flow. */
const LEGACY_VARIANT_ALIASES: Record<string, DemoVariant> = {
	prescreen: 'santos',
	good: 'whitfield',
	bad: 'malone',
	combined: 'haddad-household',
	guarantor: 'haddad-karim',
}

export type DemoReport =
	| { kind: 'detail'; data: ApplicationDetailData }
	| { kind: 'combined'; data: CombinedReportData }

export interface DemoVariantMeta {
	id: DemoVariant
	/** Sidebar title — the scrubbed applicant / household name. */
	label: string
	/** Scenario line under the title. */
	scenario: string
	/** Compact report type shown above the title in the demo picker. */
	eyebrow: 'Limited view' | 'Full' | 'Combined' | 'Backing'
	/** Sidebar dot tone. */
	tone: 'green' | 'amber' | 'red' | 'gray'
}

export interface DemoVariantGroup {
	title: string
	variants: DemoVariantMeta[]
}

const detail = (data: unknown): DemoReport => ({
	kind: 'detail',
	data: data as ApplicationDetailData,
})
const combined = (data: unknown): DemoReport => ({
	kind: 'combined',
	data: data as CombinedReportData,
})

const FIXTURES: Record<DemoVariant, DemoReport> = {
	adeyemi: detail(adeyemi),
	santos: detail(santos),
	whitfield: detail(whitfield),
	rahman: detail(rahman),
	bergeron: detail(bergeron),
	malone: detail(malone),
	'haddad-household': combined(haddadHousehold),
	'haddad-omar': detail(haddadOmar),
	'haddad-layla': detail(haddadLayla),
	'haddad-karim': detail(haddadKarim),
	'bennett-household': combined(bennettHousehold),
	'bennett-chloe': detail(bennettChloe),
	'bennett-margaret': detail(bennettMargaret),
}

const applicantName = (variant: DemoVariant) =>
	FIXTURES[variant].data.application.applicantName

/** Sidebar catalogue — public samples currently exposed in the demo picker. */
export const DEMO_VARIANT_GROUPS: DemoVariantGroup[] = [
	{
		title: 'Pre-screen',
		variants: [
			{
				id: 'santos',
				label: 'Pre-Screen',
				scenario: 'No score · full-screen offer',
				eyebrow: 'Limited view',
				tone: 'gray',
			},
		],
	},
	{
		title: 'Full screen · approved',
		variants: [
			{
				id: 'whitfield',
				label: 'Strong Applicant',
				scenario: 'Score 95 · yellow flags',
				eyebrow: 'Full',
				tone: 'green',
			},
		],
	},
	{
		title: 'Full screen · declined',
		variants: [
			{
				id: 'malone',
				label: 'High Risk',
				scenario: 'Score 45 · red & yellow flags',
				eyebrow: 'Full',
				tone: 'red',
			},
		],
	},
	{
		title: 'Household with guarantor',
		variants: [
			{
				id: 'haddad-household',
				label: 'Household',
				scenario: 'Score 63 · regular / enterprise',
				eyebrow: 'Combined',
				tone: 'amber',
			},
			{
				id: 'haddad-karim',
				label: 'Guarantor Report',
				scenario: 'Accepted',
				eyebrow: 'Backing',
				tone: 'green',
			},
		],
	},
]

export function isDemoVariant(value: unknown): value is DemoVariant {
	return (
		typeof value === 'string' &&
		(DEMO_VARIANTS as readonly string[]).includes(value)
	)
}

export function parseDemoVariant(
	value: string | null | undefined,
): DemoVariant {
	if (isDemoVariant(value)) return value
	const alias = value ? LEGACY_VARIANT_ALIASES[value] : undefined
	return alias ?? DEFAULT_DEMO_VARIANT
}

/** `?score=numeric` → Enterprise (NN/100); anything else → Standard bands. */
export function parseScoreDisplay(
	value: string | null | undefined,
): ScoreDisplay {
	return value === 'numeric' ? 'numeric' : 'band'
}

export function demoVariantMeta(variant: DemoVariant): DemoVariantMeta {
	for (const group of DEMO_VARIANT_GROUPS) {
		const meta = group.variants.find((v) => v.id === variant)
		if (meta) return meta
	}
	return {
		id: variant,
		label: applicantName(variant),
		scenario: 'Household member',
		eyebrow: 'Full',
		tone: 'amber',
	}
}

/** Standard | Enterprise switch — full-screen, combined, guarantor, student. */
export function demoShowsScoreSwitch(variant: DemoVariant): boolean {
	return variant !== 'santos'
}

/**
 * The report for a variant under the chosen score display. Fixtures are
 * cloned, then the attached HTML templates overwrite the matching variants.
 * Good and student fixtures stay on the existing overlay.
 */
export function buildDemoReport(
	variant: DemoVariant,
	scoreDisplay: ScoreDisplay,
): DemoReport {
	const fixture = FIXTURES[variant]
	const cloned: DemoReport =
		fixture.kind === 'detail'
			? { kind: 'detail', data: structuredClone(fixture.data) }
			: { kind: 'combined', data: structuredClone(fixture.data) }
	const overlaid = overlayAttachedTemplates(
		overlayHtmlSampleResults(overlayDemoFormFields(cloned, variant), variant),
		variant,
	)
	return overlaid.kind === 'detail'
		? { kind: 'detail', data: { ...overlaid.data, scoreDisplay } }
		: { kind: 'combined', data: { ...overlaid.data, scoreDisplay } }
}

/** Headline score for the sidebar (null for pre-screen / guarantor rows). */
export function demoHeadlineScore(variant: DemoVariant): number | null {
	const report = buildDemoReport(variant, 'band')
	return report.kind === 'detail'
		? (report.data.results?.overallScore ??
				report.data.application.score ??
				null)
		: (report.data.familyScore?.score ?? null)
}

/** Household a member belongs to (its combined variant), if any. */
const HOUSEHOLD_OF: Partial<Record<DemoVariant, DemoVariant>> = {
	'haddad-omar': 'haddad-household',
	'haddad-layla': 'haddad-household',
	'haddad-karim': 'haddad-household',
	'bennett-chloe': 'bennett-household',
	'bennett-margaret': 'bennett-household',
}

/**
 * Maps a fixture application id (`demo-<variant>`) back to the variant the
 * redirect routes should open: the member's own report for `individual`, or
 * its household's combined report for `combined`.
 */
export function demoVariantForApplication(
	applicationId: string | undefined,
	view: 'individual' | 'combined',
): DemoVariant | null {
	const variant = applicationId?.replace(/^demo-/, '')
	if (!isDemoVariant(variant)) return null
	if (view === 'individual') return variant
	return HOUSEHOLD_OF[variant] ?? null
}

/**
 * Demo-report URL for a variant, carrying the score switch and the return-to
 * target across the vendored components' own navigation. The redirect routes
 * only know the member id; the previous page's URL (the `Referer`) still holds
 * `score` / `returnTo`, so a member hop keeps the Standard | Enterprise choice.
 */
export function demoReportHref(
	variant: DemoVariant | null,
	referer: string | null | undefined,
): string {
	const params = new URLSearchParams()
	if (variant) params.set('variant', variant)
	try {
		const from = referer ? new URL(referer).searchParams : null
		const score = from?.get('score')
		if (score === 'numeric') params.set('score', score)
		const returnTo = from?.get('returnTo')
		if (returnTo?.startsWith('/') && !returnTo.startsWith('//')) {
			params.set('returnTo', returnTo)
		}
	} catch {
		// malformed referer → plain link
	}
	const query = params.toString()
	return query ? `/demo-report?${query}` : '/demo-report'
}
